import React, { useEffect, useMemo, useState } from 'react';
import { QueueUxPreferences, trackWarning } from 'services';
import { TagPicker, IBasePicker, getTheme } from '@fluentui/react';
import { Opinion } from '../../../services/feedback.model';
import { ITag } from '@fluentui/react/lib/Pickers';
import TraitDefinitions from '../../review/TraitDefinitions';

export const traitJoiningString = '_';

const tagPickerHolder: React.CSSProperties = {
  maxWidth: 500
};

interface SideBarTagPickerProps {
  onComponentChange(opinion: Opinion): void;
  onTraitSelection?(trait): void;
  selectedTrait?: string[];
  uxPreferences: QueueUxPreferences;
  labelsData: {
    [id: string]: string[];
  };
  isPickerDisabled: boolean | undefined;
  focusRef: React.RefObject<IBasePicker<ITag>>;
  renderTraitDefinitions?: boolean;
}

export const SidebarTagPicker: React.FunctionComponent<SideBarTagPickerProps> = props => {
  const { palette, fonts } = getTheme();
  const categories = Object.keys(props.labelsData);
  const renderTraitDefinitions = props.renderTraitDefinitions;
  const [selectedItems, setSelectedItems] = useState<ITag[]>([]);
  const [hoverTrait, setHoverTrait] = useState<string>('');
  useEffect(() => {
    if (props.isPickerDisabled && selectedItems.length) {
      setSelectedItems([]);
    }
  }, [props.isPickerDisabled]);

  const formatDisplayItems = (): ITag[] => {
    const { labelsTop, categoriesTop } = props.uxPreferences.decide;
    let tempCategories: string[] = Object.keys(props.labelsData);
    const traitSuggestions: { key: string; name: string }[] = [];

    if (labelsTop?.length && categoriesTop?.length) {
      trackWarning('SidebarTagPicker: user supplied both labelsTop and categoriesTop.');
    } else if (labelsTop?.length && tempCategories.length > 1) {
      trackWarning('SidebarTagPicker: user supplied labelsTop with more than one category.');
    } else if (labelsTop?.length && tempCategories.length < 1) {
      trackWarning('SidebarTagPicker: user supplied labelsTop with no categories.');
    } else if (labelsTop?.length) {
      // add the single category
      traitSuggestions.push({ key: 'category', name: tempCategories[0] });
      // add top labels
      labelsTop.forEach(trait => {
        if (props.labelsData[tempCategories[0]].includes(trait)) {
          traitSuggestions.push({ key: tempCategories[0], name: trait });
        }
      });
      // add the regular labels
      props.labelsData[tempCategories[0]].forEach(trait => {
        if (!labelsTop.includes(trait)) {
          traitSuggestions.push({ key: tempCategories[0], name: trait });
        }
      });
    } else if (categoriesTop?.length) {
      const orderedCategories: string[] = [];
      // add the categoriesTop to the top of the array
      categoriesTop.forEach(categoryTop => {
        const index = tempCategories.indexOf(categoryTop);
        if (index > -1) {
          orderedCategories.push(categoryTop);
          tempCategories.splice(index, 1);
        }
      });

      // add the other tempCategories to the bottom
      tempCategories = orderedCategories.concat(tempCategories);
    }

    if (!traitSuggestions.length) {
      tempCategories.forEach(key => {
        // label group
        traitSuggestions.push({ key: 'category', name: key });
        props.labelsData[key].forEach(name => {
          traitSuggestions.push({ key, name });
        });
      });
    }

    return traitSuggestions.map(item => ({ key: item.key + traitJoiningString + item.name, name: item.name }));
  };

  const displayItems = useMemo(formatDisplayItems, []);

  const categoryStyle: React.CSSProperties = {
    color: palette.neutralDark,
    fontSize: fonts.small.fontSize
  };

  const itemStyle: React.CSSProperties = {
    color: palette.black,
    fontSize: fonts.medium.fontSize
  };

  const onRenderSuggestionsItem = (props: ITag): JSX.Element => {
    if (typeof props.key === 'number' || props.key.includes('category' + traitJoiningString)) {
      return (
        <span id="category" className="pl-2 py-2" style={categoryStyle}>
          {props.name}
        </span>
      );
    } else if (renderTraitDefinitions) {
      return <TraitDefinitions name={props.name} hoverTrait={hoverTrait} setHoverTrait={setHoverTrait} />;
    } else {
      return (
        <span className="pl-3 py-2" style={itemStyle}>
          {props.name}
        </span>
      );
    }
  };

  // TODO: get rid of this hack
  // https://microsoft.visualstudio.com/Xbox/_workitems/edit/38064449
  // I think we can likely replace TagPicker with a Dropdown and get it to work cleanly without this wonk
  // What's happening is that we're reaching into fluent ui to disable the onhover feature of the category heads
  // in the TagPicker, something that Dropdown supports natively
  const applyStyle = (): void => {
    const selectionButton = document.querySelectorAll('#category');
    if (selectionButton != null) {
      selectionButton.forEach(item => {
        if (item.parentElement && item.parentElement.parentElement) {
          item.parentElement.parentElement.style.pointerEvents = 'none';
        }
      });
    }
  };

  // create map function which will assign all values from labelData into item:ITAG
  const onFilterChanged = (filterText: string): ITag[] => {
    if (!filterText) {
      return [];
    }

    const searchValue = filterText.trim();
    const resultList: ITag[] = [];
    categories.forEach(key => {
      const labels = props.labelsData[key];
      // If search value matches the category.
      if (stringCheck(searchValue, key)) {
        labels.forEach(label => resultList.push({ key: key + traitJoiningString + label, name: label }));
      } else {
        // If search value matches a label.
        const labelFilterResult: string[] = labels.filter(label => stringCheck(searchValue, label));
        if (labelFilterResult.length > 0) {
          labelFilterResult.forEach(label => resultList.push({ key: key + traitJoiningString + label, name: label }));
        }
      }
    });

    return resultList.sort((a, b) => a.name.localeCompare(b.name));
  };

  const stringCheck = (searchValue: string, label: string): boolean => {
    return label.toLowerCase().includes(searchValue.toLowerCase());
  };

  const onChange = (items?: ITag[]): void => {
    if (!items) {
      return;
    }
    const currentTrait: string[] = [];
    const opinionObj = new Opinion();
    items.forEach(item => {
      if (typeof item.key === 'number' || item.key.includes('category' + traitJoiningString)) {
        return;
      }
      const category: string = item.key.split(traitJoiningString)[0];
      // If category isn't currently in opinionCategoryLabels then initialize array
      if (opinionObj.opinionCategoryLabels[category] == null) {
        opinionObj.opinionCategoryLabels[category] = [];
      }
      // Add label to list if it doesn't already exist.
      if (!opinionObj.opinionCategoryLabels[category].includes(item.name)) {
        opinionObj.opinionCategoryLabels[category].push(item.name);
        currentTrait.push(`${category}${traitJoiningString}${item.name}`);
      }
    });
    {
      props.onTraitSelection && props.onTraitSelection(currentTrait);
    }
    props.onComponentChange(opinionObj);
  };

  const onItemSelected = (item?: ITag): ITag | null => {
    // If selected item was added to tagList return null
    if (item) {
      if (
        typeof item.key === 'number' ||
        item.key.includes('category' + traitJoiningString) ||
        (props.focusRef.current && listContainsDocument(item, props.focusRef.current.items))
      ) {
        return null;
      }
      return item;
    }
    return null;
  };

  const listContainsDocument = (tag: ITag, tagList?: ITag[]): boolean => {
    if (!tagList || !tagList.length || tagList.length === 0) {
      return false;
    }
    return tagList.filter(compareTag => compareTag.key === tag.key).length > 0;
  };

  const generateDefaultSelectedItems = () => {
    if (props.selectedTrait) {
      return props.selectedTrait.map(
        (item): ITag => {
          return { name: item.split(traitJoiningString)[1], key: item.split(traitJoiningString)[0] };
        }
      );
    }
  };

  return (
    <div onClick={applyStyle} style={tagPickerHolder}>
      <TagPicker
        selectedItems={selectedItems}
        removeButtonAriaLabel="Remove"
        componentRef={props.focusRef}
        onResolveSuggestions={onFilterChanged}
        onRenderSuggestionsItem={onRenderSuggestionsItem}
        onEmptyResolveSuggestions={() => displayItems}
        onItemSelected={onItemSelected}
        pickerSuggestionsProps={{
          suggestionsContainerAriaLabel: 'Traits list',
          noResultsFoundText: 'No traits'
        }}
        onChange={(items?: ITag[]): void => {
          onChange(items);
          setSelectedItems(items || []);
        }}
        itemLimit={props.uxPreferences.decide.maxAllowedLabels}
        disabled={props.isPickerDisabled}
        inputProps={{
          placeholder: 'Add traits',
          required: true,
          id: 'traitsInput'
        }}
        defaultSelectedItems={generateDefaultSelectedItems()}
      />
    </div>
  );
};
